"use client"

import { useState } from "react"
import Link from "next/link"
import { Loader2, Truck } from "lucide-react"

import { cn } from "@/lib/utils"
import { PageHero } from "@/components/sections/page-hero"
import { CtaBanner } from "@/components/sections/cta-banner"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { AnimateIn } from "@/components/ui/animate-in"
import { TextMorph } from "@/components/ui/text-morph"

export default function DemandeRamassagePage() {
  const [submitting, setSubmitting] = useState(false)

  // Local-only state pour bloquer les double-clicks pendant la requête
  // network. La submission elle-même reste un POST natif (le backend
  // /api/contact-ramassage existe ailleurs) — ici on se contente
  // d'éviter le multi-submit + de donner un feedback visuel.
  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    if (submitting) {
      e.preventDefault()
      return
    }
    setSubmitting(true)
    // Réinitialise après ~3s pour les cas où la requête échoue
    // silencieusement — l'utilisateur peut retenter.
    setTimeout(() => setSubmitting(false), 3000)
  }

  return (
    <>
      <PageHero
        badge="Service"
        title="Demande de ramassage"
        description="Remplissez le formulaire ci-dessous pour planifier un ramassage de vos produits bois et dérivés."
      />

      <section className="section-normal px-4 md:px-6">
        <div className="mx-auto max-w-2xl">
          <AnimateIn>
            <div
              className={cn(
                "rounded-[2rem] p-1.5",
                "bg-foreground/[0.02] ring-1 ring-foreground/5",
                "dark:bg-background/[0.06] dark:ring-foreground/10"
              )}
            >
              <div className="rounded-[calc(2rem-0.375rem)] bg-card p-6 md:p-8">
                <div className="mb-8 flex items-center gap-3">
                  <div className="flex size-10 items-center justify-center rounded-full bg-primary/10">
                    <Truck className="size-5 text-primary" />
                  </div>
                  <h2 className="text-xl font-bold tracking-tight">
                    Demande de ramassage
                  </h2>
                </div>

                <form
                  className="space-y-6"
                  onSubmit={(e) => {
                    e.preventDefault()
                    handleSubmit(e)
                  }}
                >
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="name">Nom Prénom *</Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        placeholder="Votre nom et prénom"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="entreprise">Entreprise</Label>
                      <Input
                        id="entreprise"
                        name="entreprise"
                        type="text"
                        placeholder="Nom de votre entreprise"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Téléphone *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="Votre numéro de téléphone"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="Votre adresse email"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="adresse">Adresse de ramassage</Label>
                    <Input
                      id="adresse"
                      name="adresse"
                      type="text"
                      placeholder="Adresse complète de ramassage"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Message / details de la demande</Label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="Décrivez votre demande de ramassage (type de produits, quantité, date souhaitée...)"
                      className="min-h-32"
                    />
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    disabled={submitting}
                    className="w-full rounded-full active:scale-[0.97]"
                  >
                    {submitting ? (
                      <Loader2 className="mr-2 size-4 animate-spin" />
                    ) : null}
                    <TextMorph as="span" duration={400}>
                      {submitting ? "Envoi…" : "Envoyer la demande"}
                    </TextMorph>
                  </Button>
                </form>
              </div>
            </div>
          </AnimateIn>

          <AnimateIn delay={100}>
            <div className="mt-10 space-y-4 text-sm leading-relaxed text-muted-foreground">
              <h3 className="text-base font-semibold text-foreground">Comment fonctionne le service de ramassage ?</h3>
              <p>
                ADIS 95 propose un service de ramassage gratuit pour les professionnels du bois en
                Île-de-France. Notre camion effectue des tournées régulières dans le Val-d&apos;Oise (95),
                les Yvelines (78), les Hauts-de-Seine (92), la Seine-Saint-Denis (93), le Val-de-Marne (94)
                et Paris (75). Nous récupérons vos outils à affûter directement sur votre lieu de travail :
                lames circulaires, fraises, fers de raboteuse, chaînes de tronçonneuse et tout autre
                outillage coupant.
              </p>
              <p>
                Une fois le ramassage effectué, vos outils sont traités dans notre atelier de 600&nbsp;m²
                à Méry-sur-Oise par nos techniciens spécialisés. Nous vous recontactons dès que vos outils
                sont prêts et organisons la livraison de retour. Les délais habituels sont de 48 à 72 heures
                pour l&apos;affûtage standard, et 24 heures en service express pour les urgences.
              </p>
              <p>
                Pour toute question sur ce service, vous pouvez également nous contacter par téléphone
                au <a href="tel:+33130370530" className="font-medium text-primary underline underline-offset-4">01 30 37 05 30</a> ou
                passer directement à notre{" "}
                <Link href="/contact" className="font-medium text-primary underline underline-offset-4">
                  atelier
                </Link>.
              </p>
            </div>
          </AnimateIn>

          <AnimateIn delay={150}>
            <div className="mt-12 rounded-xl border border-primary/20 bg-primary/5 p-6 text-center">
              <h3 className="text-lg font-bold">Connexion revendeur</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Vous êtes revendeur ou professionnel partenaire ? Accédez à votre espace dédié pour gérer vos demandes de ramassage.
              </p>
              <Button asChild className="mt-4 rounded-full active:scale-[0.97]">
                <Link href="/contact">
                  Accéder à l&apos;espace revendeur
                </Link>
              </Button>
            </div>
          </AnimateIn>
        </div>
      </section>

      <CtaBanner />
    </>
  )
}
